// HL-11003 — withhold the catalog auto-discount from returning customers.
//
// The arm lives in localStorage (`abtest_auto_catalog_coupon`), not on the cart row: rolled here
// on the shipping page, then posted to the API, which reconciles the cart in whichever direction the
// arm implies. Editing the key in devtools and reloading is enough to switch flows.

import { ABTestManager } from "../../../../utils/ABTestManager";
import {
  ABTEST_AUTO_CATALOG_COUPON_NAME,
  ABTEST_AUTO_CATALOG_COUPON_VARIANT,
  ABTEST_AUTO_CATALOG_COUPON_VARIANTS,
} from "../../../../utils/consts";
import type { AbtestAutoCatalogCouponVariant } from "../../../../utils/consts";

/** The endpoint that reconciles the cart both ways ('b' removes the discount, 'a' puts it back). */
export const ABTEST_AUTO_CATALOG_COUPON_ENDPOINT = "cart/abtest-auto-catalog-coupon";

// Must match the key ABTestManager derives from its default `abtest_` prefix — this is the key to edit
// in devtools to switch flows.
export const ABTEST_AUTO_CATALOG_COUPON_STORAGE_KEY = `abtest_${ABTEST_AUTO_CATALOG_COUPON_NAME}`;

export type DiscountCartLike = {
  auto_bonus?: number | boolean | null;
  bonus_id?: number | null;
  bonus_value?: number | string | null;
  applied_subscription_discount?: number | null;
  only_prescription?: number | boolean | null;
};

// Shared shape of a catalog auto-discount cart: the auto bonus is flagged, no coupon owns the
// discount, and nothing else (subscription, prescription-only) already suppresses it.
const isCatalogAutoBonusCart = (cart: DiscountCartLike): boolean =>
  Boolean(cart.auto_bonus)
  && !cart.bonus_id
  && !cart.applied_subscription_discount
  && !cart.only_prescription;

/** Mirrors the server's hasCatalogAutoDiscount — the discount is currently applied. */
export const hasCatalogAutoDiscount = (cart: DiscountCartLike): boolean =>
  isCatalogAutoBonusCart(cart) && Number(cart.bonus_value ?? 0) > 0;

/** Mirrors the server's isCatalogAutoDiscountWithheld — this test already stripped the discount. */
export const isCatalogAutoDiscountWithheld = (cart: DiscountCartLike): boolean =>
  isCatalogAutoBonusCart(cart) && Number(cart.bonus_value ?? 0) === 0;

/**
 * Both states count. A participant in arm 'b' comes back with bonus_value 0 on every reload — gate on
 * hasCatalogAutoDiscount alone and they'd silently drop out of the experiment, losing their tracking
 * property and any way back to arm 'a'.
 */
export const isDiscountedCatalogCart = (cart: DiscountCartLike): boolean =>
  hasCatalogAutoDiscount(cart) || isCatalogAutoDiscountWithheld(cart);

/** The stored arm, or null when nothing valid is stored. Mirrors ABTestManager's own load-and-validate. */
export const readStoredAutoCatalogCouponVariant = (): AbtestAutoCatalogCouponVariant | null => {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem(ABTEST_AUTO_CATALOG_COUPON_STORAGE_KEY);
  return ABTEST_AUTO_CATALOG_COUPON_VARIANTS.includes(stored as AbtestAutoCatalogCouponVariant)
    ? (stored as AbtestAutoCatalogCouponVariant)
    : null;
};

export const isAbtestAutoCatalogCouponParticipant = (args: {
  /** The shipping GET's abtestAutoCatalogCouponEnabled — the catalog is flagged for the test in the DB. */
  isCatalogEnabled: boolean;
  isReturningCustomer?: boolean | null;
  cart: DiscountCartLike;
  weight: number;
}): boolean =>
  args.isCatalogEnabled
  && args.isReturningCustomer === true
  // The weight only decides who gets *enrolled*. Someone already holding an arm stays in the test, so
  // their cart keeps being reconciled to it — otherwise switching the test off would strand every arm
  // 'b' cart with the discount removed and no way back, and editing the key by hand would do nothing.
  && (args.weight > 0 || readStoredAutoCatalogCouponVariant() !== null)
  && isDiscountedCatalogCart(args.cart);

/** Normalises the raw shipping-GET weight to a usable 0..1 probability (0 = test switched off). */
export const resolveAutoCatalogCouponWeight = (rawWeight: unknown): number => {
  const weight = Number(rawWeight);
  if (!Number.isFinite(weight) || weight <= 0) return 0;
  return Math.min(weight, 1);
};

/**
 * ABTestManager already does the persistence: it stores under `abtest_<testName>`, returns the stored
 * arm on later visits instead of re-rolling, and discards a stored value that isn't a known variant.
 */
export const resolveAutoCatalogCouponVariant = (weight: number): AbtestAutoCatalogCouponVariant =>
  new ABTestManager({
    testName: ABTEST_AUTO_CATALOG_COUPON_NAME,
    variants: [...ABTEST_AUTO_CATALOG_COUPON_VARIANTS],
    weights: [1 - weight, weight],
  }).getVariant() as AbtestAutoCatalogCouponVariant;

export const isAbtestAutoCatalogCouponArm = (variant: string | null) =>
  variant === ABTEST_AUTO_CATALOG_COUPON_VARIANT.TEST;
